// Common Setup enable/disable per operation position (OP10, OP20, ...).
// One shared setting per position — applies to that position across all Work Orders,
// regardless of which specific task is routed there. Stored in TB customer attribute 'op_setup_master'.
import React, { useEffect, useState } from 'react';
import { customerbasedshift, cleanCustomerId } from '../../Services/app/operatorservice';
import { shiftadd } from '../../Services/app/masterservice';
import { updateOperationSetupByPosition } from '../../Services/app/workorderservice';

const BR = {
  orange: '#f97316', dark: '#111827', muted: '#6b7280', border: '#e5e7eb',
  bg: '#f9fafb', green: '#16a34a', red: '#dc2626',
};

const DEFAULT_OPS = Array.from({ length: 9 }, (_, i) => ({ op_seq: `OP${(i + 1) * 10}`, setup_enabled: true }));

const seqNum = (op_seq) => parseInt(String(op_seq || '').replace(/\D/g, ''), 10) || 0;

export default function OperationSetup() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSeq, setNewSeq] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await customerbasedshift(customerId, 'op_setup_master');
      const val = Array.isArray(r?.[0]?.value) ? r[0].value : [];
      if (val.length) {
        setOps(val);
      } else {
        await shiftadd({ op_setup_master: DEFAULT_OPS }, customerId, 'SERVER_SCOPE');
        setOps(DEFAULT_OPS);
      }
    } catch (e) {
      console.error(e);
      setOps(DEFAULT_OPS);
    }
    setLoading(false);
  }

  async function persist(next) {
    setOps(next);
    setSaving(true);
    try {
      await shiftadd({ op_setup_master: next }, customerId, 'SERVER_SCOPE');
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  }

  const toggle = (op_seq) => {
    const next = ops.map(o => o.op_seq === op_seq ? { ...o, setup_enabled: !(o.setup_enabled !== false) } : o);
    const updated = next.find(o => o.op_seq === op_seq);
    persist(next);
    // "OP10 means all OP10" — push the flip onto every real work order operation at this
    // position too, so the operator screen's setup gate (which reads the SQL row, not this
    // list) actually reflects it, not just this screen's own display.
    updateOperationSetupByPosition(customerId, op_seq, updated.setup_enabled !== false).catch(e => console.error('setup-by-position update failed', e));
  };

  const addOp = () => {
    const seq = newSeq.trim().toUpperCase();
    if (!seq || ops.some(o => o.op_seq === seq)) { setNewSeq(''); return; }
    setNewSeq('');
    persist([...ops, { op_seq: seq, setup_enabled: true }].sort((a, b) => seqNum(a.op_seq) - seqNum(b.op_seq)));
  };

  const removeOp = (op_seq) => persist(ops.filter(o => o.op_seq !== op_seq));

  return (
    <div style={{ padding: 18, background: BR.bg, minHeight: '100vh' }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: BR.dark }}>Operation Setup</div>
        <div style={{ fontSize: 12, color: BR.muted, marginTop: 2 }}>
          Enable or disable the setup step per operation position (OP10, OP20…) — one shared setting per position, applied across all Work Orders.
        </div>
      </div>

      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden', maxWidth: 520 }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: BR.muted }}>Loading…</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 800, color: BR.muted, textTransform: 'uppercase' }}>Operation</th>
                <th style={{ padding: '10px 14px', textAlign: 'center', fontSize: 11, fontWeight: 800, color: BR.muted, textTransform: 'uppercase' }}>Setup</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {ops.map(o => (
                <tr key={o.op_seq} style={{ borderBottom: `1px solid ${BR.border}` }}>
                  <td style={{ padding: '10px 14px', fontWeight: 700, fontFamily: 'monospace' }}>{o.op_seq}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input type="checkbox" checked={o.setup_enabled !== false} onChange={() => toggle(o.op_seq)} />
                    </label>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button onClick={() => removeOp(o.op_seq)} title="Remove"
                      style={{ background: 'none', border: 'none', color: BR.red, cursor: 'pointer', fontSize: 15 }}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: `1px solid ${BR.border}` }}>
          <input value={newSeq} onChange={e => setNewSeq(e.target.value)} placeholder="e.g. OP100"
            onKeyDown={e => e.key === 'Enter' && addOp()}
            style={{ flex: 1, padding: '7px 9px', border: `1px solid ${BR.border}`, borderRadius: 5, fontSize: 13 }} />
          <button onClick={addOp} style={{
            padding: '7px 14px', background: BR.orange, color: '#fff', border: 'none',
            borderRadius: 5, fontWeight: 700, fontSize: 13, cursor: 'pointer'
          }}>+ Add</button>
        </div>
      </div>
      {saving && <div style={{ marginTop: 8, fontSize: 11, color: BR.muted }}>Saving…</div>}
    </div>
  );
}
